/**
 * Finding a journal in the list — the search box and the sort control share
 * this so matching is identical wherever it runs.
 *
 * Matching follows the blending log, which follows the recipe list: case- and
 * accent-insensitive prefix matching, AND across query tokens. The haystack is
 * the journal's name, its tags, the prose of its text blocks, what each
 * measurement was taken *of* — "Must", "Brine", "Jar 2" — and the names of the
 * batches and blends its blocks point at. Those are what a brewer types when they are
 * looking for "the one where I wrote about the cherries", or for whatever they
 * wrote up about the pale ale.
 *
 * The names come in from the caller rather than out of a document, because a
 * journal holds ids and only the workspace knows what they are called. The list
 * already has both maps for the row it is drawing.
 *
 * A measurement's `note` stays out, for the reason a blend's notes do: it is
 * the afterthought beside a number rather than a name, and folding it in makes
 * a short query match half the list.
 */

import { normalize } from '@beerwright/catalog';
import type { JournalDoc } from '@beerwright/commands';
import { naturalCompare } from '@/lib/natural-sort';

/**
 * What a journal is about: the batches and blends its blocks name (D-M1).
 *
 * Distinct, in stack order, because that is the order the page reads in and the
 * order the list row says them in. A page with a recipe and a chart for the
 * same batch is about one batch, once; a block that has not chosen yet is about
 * nothing and is skipped.
 *
 * Pure, and the one answer to "what is this journal about" — the reverse-link
 * hooks, the list row and the search all read it, so the batch page, the blend
 * page and the line under the name cannot disagree.
 */
export function journalReferences(journal: JournalDoc): {
  readonly batchIds: readonly string[];
  readonly blendIds: readonly string[];
} {
  const batchIds: string[] = [];
  const blendIds: string[] = [];
  for (const block of journal.blocks) {
    if (block.type === 'batch-recipe' || block.type === 'batch-telemetry') {
      if (block.batchId !== null && !batchIds.includes(block.batchId)) batchIds.push(block.batchId);
    } else if (block.type === 'blend-summary') {
      if (block.blendId !== null && !blendIds.includes(block.blendId)) blendIds.push(block.blendId);
    }
  }
  return { batchIds, blendIds };
}

/**
 * What a journal's blocks are called, in the order the row says them.
 *
 * Batches and then blends, each in stack order, and only the ones this
 * workspace still holds: an id whose document has been deleted has no name to
 * print, and the block that points at it already says so on the page.
 */
export function journalReferenceNames(
  journal: JournalDoc,
  names: JournalReferenceNames,
): readonly string[] {
  const { batchIds, blendIds } = journalReferences(journal);
  return [
    ...batchIds.map((id) => names.batches.get(id)),
    ...blendIds.map((id) => names.blends.get(id)),
  ].filter((name): name is string => name !== undefined);
}

/** What the workspace calls the documents a journal's blocks point at. */
export interface JournalReferenceNames {
  readonly batches: ReadonlyMap<string, string>;
  readonly blends: ReadonlyMap<string, string>;
}

/**
 * The batch a journal most recently referred to, or `null`.
 *
 * D-M2's default, and the reason the common case is still one tap after the
 * link moved onto the block (D-M1): a recipe or chart block added to a
 * single-batch journal starts on the batch the page is already about, so the
 * brewer is asked once and never again. The *last* block that names one rather
 * than the first, because a page that has moved on to a second batch has that
 * batch at the bottom of the stack.
 */
export function lastBatchIdOf(journal: JournalDoc): string | null {
  let found: string | null = null;
  for (const block of journal.blocks) {
    if (block.type !== 'batch-recipe' && block.type !== 'batch-telemetry') continue;
    if (block.batchId !== null) found = block.batchId;
  }
  return found;
}

/**
 * The blending session a journal most recently referred to, or `null`.
 *
 * {@link lastBatchIdOf}'s twin, for the same reason: a page that is the story of
 * one blend asks which blend once.
 */
export function lastBlendIdOf(journal: JournalDoc): string | null {
  let found: string | null = null;
  for (const block of journal.blocks) {
    if (block.type === 'blend-summary' && block.blendId !== null) found = block.blendId;
  }
  return found;
}

/** Split a string into the tokens {@link journalMatchesQuery} compares. */
function tokensOf(value: string): readonly string[] {
  const normalized = normalize(value);
  return normalized.length === 0 ? [] : normalized.split(' ');
}

/** Name, prose, what was measured, and what it is about. See the header. */
function searchParts(
  journal: JournalDoc,
  names: JournalReferenceNames | undefined,
): readonly string[] {
  const parts: string[] = [journal.name, ...journal.tags];
  for (const block of journal.blocks) {
    if (block.type === 'text') parts.push(block.body);
    else if (block.type === 'measurement') parts.push(block.label);
  }
  if (names !== undefined) parts.push(...journalReferenceNames(journal, names));
  return parts;
}

/**
 * True when every query token prefixes some token in the journal's name, its
 * prose, a measurement label, or the name of a batch or blend it is about. An
 * empty (or whitespace-only) query matches everything.
 */
export function journalMatchesQuery(
  journal: JournalDoc,
  query: string,
  names?: JournalReferenceNames,
): boolean {
  const queryTokens = tokensOf(query);
  if (queryTokens.length === 0) return true;
  const haystack = searchParts(journal, names).flatMap(tokensOf);
  return queryTokens.every((token) => haystack.some((part) => part.startsWith(token)));
}

/**
 * True when the journal carries every tag named. An empty list requires
 * nothing, which is what an untouched chip row means (`recipeHasTags`'s rule).
 */
export function journalHasTags(journal: JournalDoc, tags: readonly string[]): boolean {
  if (tags.length === 0) return true;
  return tags.every((tag) => journal.tags.includes(tag));
}

/** Journals matching a free-text query and an optional set of required tags. */
export function filterJournals(
  journals: readonly JournalDoc[],
  options: {
    readonly query?: string;
    readonly tags?: readonly string[];
    readonly names?: JournalReferenceNames;
  } = {},
): readonly JournalDoc[] {
  const query = options.query ?? '';
  const tags = options.tags ?? [];
  return journals.filter(
    (journal) =>
      journalMatchesQuery(journal, query, options.names) && journalHasTags(journal, tags),
  );
}

/** How the journal list can be ordered. Default is last updated — newest edit first. */
export const JOURNAL_SORTS = ['updated', 'created', 'name'] as const;

/** @see JOURNAL_SORTS */
export type JournalSort = (typeof JOURNAL_SORTS)[number];

/** Labels for the sort control. */
export const JOURNAL_SORT_LABELS: Readonly<Record<JournalSort, string>> = {
  updated: 'Last updated',
  created: 'Date started',
  name: 'Name',
};

const COMPARE: Readonly<Record<JournalSort, (a: JournalDoc, b: JournalDoc) => number>> = {
  updated: (a, b) => b.updatedAt - a.updatedAt || b.createdAt - a.createdAt,
  created: (a, b) => b.createdAt - a.createdAt || naturalCompare(a.name, b.name),
  name: (a, b) => naturalCompare(a.name, b.name) || b.updatedAt - a.updatedAt,
};

/**
 * Order a journal list.
 *
 * `updated` is the default: the ferment you wrote about this morning stays at
 * the top. `created` is the one the blending log does not have and a journal
 * wants, because a journal is a story with a start date and "which did I begin
 * first" is a question about a cellar of them. Name is for scanning.
 */
export function sortJournals(
  journals: readonly JournalDoc[],
  sort: JournalSort,
): readonly JournalDoc[] {
  return [...journals].sort(COMPARE[sort]);
}
